import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, CircleHelp, Loader2, Search, ShieldCheck, X } from 'lucide-react';
import { ActionMenu } from '../../../../components/ActionMenu';
import { AppUser, UserType } from '../../../../types';
import { fetchUsers, updateUserType } from '../../../../services/users';
import { useAuth } from '../../../../contexts/AuthContext';
import { logGlobalError } from '../../../../utils/globalErrorLogger';

const USER_TYPES: Array<{ id: UserType; label: string; tone: string }> = [
  { id: 'sales', label: 'Sales', tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  { id: 'admin', label: 'Admin', tone: 'bg-purple-50 text-purple-800 border-purple-200' },
  { id: 'unknown', label: 'Unknown', tone: 'bg-gray-50 text-gray-700 border-gray-200' },
];

export function UsersTool({ mobileNavTrigger }: { mobileNavTrigger?: React.ReactNode }) {
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [query, setQuery] = useState('');
  const [pendingUid, setPendingUid] = useState<string | null>(null);

  async function loadUsers() {
    setBusy(true);
    setError(null);
    try {
      setUsers(await fetchUsers());
    } catch (err: any) {
      setError(err?.message || 'Failed to load users');
      logGlobalError(err, { context: 'AdminUsers.loadUsers' });
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void loadUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return users;
    return users.filter((user) =>
      [user.displayName, user.email, user.userType]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(trimmed)),
    );
  }, [query, users]);

  async function changeUserType(uid: string, userType: UserType) {
    setPendingUid(uid);
    setError(null);
    try {
      await updateUserType(uid, userType);
      setUsers((current) =>
        current.map((user) =>
          user.uid === uid
            ? { ...user, userType, isAdmin: userType === 'admin' }
            : user,
        ),
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to update user');
      logGlobalError(err, {
        context: 'AdminUsers.changeUserType',
        metadata: { targetUid: uid, userType },
      });
    } finally {
      setPendingUid(null);
    }
  }

  return (
    <section className="min-w-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">Users</h1>
          <p className="mt-1 text-sm text-gray-500">
            Set each user account type. Unknown users cannot access the CRM workspace.
          </p>
        </div>
        {mobileNavTrigger}
      </div>

      {error && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search users..."
            aria-label="Search users"
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </div>
      </div>

      {busy ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="relative mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Current type</th>
                  <th className="px-3 py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <UserRow
                    key={user.uid}
                    user={user}
                    isSelf={currentUser?.uid === user.uid}
                    pending={pendingUid === user.uid}
                    onChangeUserType={(next) => changeUserType(user.uid, next)}
                  />
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="p-6 text-sm text-gray-500">
                      No users match the current search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function UserRow({
  user,
  isSelf,
  pending,
  onChangeUserType,
}: {
  user: AppUser;
  isSelf: boolean;
  pending: boolean;
  onChangeUserType: (userType: UserType) => void;
}) {
  const spec = USER_TYPES.find((item) => item.id === user.userType) ?? USER_TYPES[2];
  return (
    <tr className="align-top">
      <td className="min-w-[220px] px-3 py-2">
        <div className="truncate font-medium text-gray-900">
          {user.displayName || '(no name)'}
          {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
        </div>
        <div className="truncate text-xs text-gray-500">{user.email}</div>
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${spec.tone}`}>
          {spec.label}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        <ActionMenu
          label={`Actions for ${user.displayName || user.email || 'user'}`}
          disabled={pending}
          items={USER_TYPES.filter((item) => item.id !== user.userType).map((item) => ({
            id: item.id,
            label: `Set as ${item.label}`,
            icon: item.id === 'sales'
              ? <Briefcase className="h-4 w-4" />
              : item.id === 'admin'
                ? <ShieldCheck className="h-4 w-4" />
                : <CircleHelp className="h-4 w-4" />,
            onSelect: () => onChangeUserType(item.id),
          }))}
        />
      </td>
    </tr>
  );
}
